import moment from "moment";
import { makeStyles } from "tss-react/mui";
import { MiButtonGroup, MiDateRangePicker } from "@miplatform/components";
import { IDateRangeISO } from "@miplatform/types";
import {
  addToDate,
  getDate,
  getDateISO,
  subtractFromDate,
} from "@miplatform/utils";
import { Typography } from "@mui/material";
import { DatePicker } from "../DatePicker/DatePicker";

enum DayView {
  SINGLE = "1 Day",
  THREE = "3 Days",
}

export const MiWorkBoardHeader = ({
  title,
  dateRange,
  onDateChange,
  className = "",
}: {
  title: string;
  dateRange: IDateRangeISO;
  onDateChange: (dateRange: IDateRangeISO) => void;
  className?: string;
}) => {
  const { classes } = useStyles();

  const is3DayView = !!dateRange.endDate;

  const handleDayViewChange = (view: DayView.SINGLE | DayView.THREE) => {
    if (view === DayView.THREE) {
      const startDate = subtractFromDate(dateRange.startDate, 1);
      const endDate = addToDate(dateRange.startDate, 1);
      onDateChange({
        startDate: getDateISO(startDate),
        endDate: getDateISO(endDate),
      });
    } else {
      const newDate = dateRange.endDate
        ? getDate(dateRange.endDate).subtract(1, "days")
        : getDate(dateRange.startDate);
      onDateChange({
        startDate: getDateISO(newDate),
        endDate: null,
      });
    }
  };

  const handleDateRangeChange = (newStartDate: Date) => {
    const endDate = addToDate(newStartDate, 2);

    onDateChange({
      startDate: newStartDate.toISOString(),
      endDate: endDate?.toISOString(),
    });
  };

  const handleRangeRightArrowClick = () => {
    const newStartDate = addToDate(dateRange.startDate, 1);
    const newEndDate = addToDate(dateRange.startDate, 3);
    onDateChange({
      startDate: getDateISO(newStartDate),
      endDate: getDateISO(newEndDate),
    });
  };

  const handleRangeLeftArrowClick = () => {
    const newStartDate = subtractFromDate(dateRange.startDate, 1);
    const newEndDate = addToDate(dateRange.startDate, 1);
    onDateChange({
      startDate: getDateISO(newStartDate),
      endDate: getDateISO(newEndDate),
    });
  };

  return (
    <div className={`${className} ${classes.headerContainer} `}>
      <Typography variant="h5">{title}</Typography>
      {!dateRange.endDate ? (
        <DatePicker
          date={getDate(dateRange.startDate)}
          setDate={(date: string) =>
            onDateChange({ startDate: date, endDate: null })
          }
        />
      ) : (
        <MiDateRangePicker
          onDateChange={handleDateRangeChange}
          dateRange={{
            startDate: moment(dateRange.startDate).toDate(),
            endDate: dateRange.endDate
              ? moment(dateRange.endDate).toDate()
              : moment(dateRange.startDate).toDate(),
          }}
          exactRangeLength={3}
          calendarCount={1}
          showThisWeekButton={false}
          onRightArrowClick={handleRangeRightArrowClick}
          onLeftArrowClick={handleRangeLeftArrowClick}
          dateFormat="MMM DD"
        />
      )}
      <MiButtonGroup
        data={[
          {
            label: DayView.SINGLE,
            value: DayView.SINGLE,
          },
          {
            label: DayView.THREE,
            value: DayView.THREE,
          },
        ]}
        selected={is3DayView ? DayView.THREE : DayView.SINGLE}
        handleClick={() => {
          handleDayViewChange(is3DayView ? DayView.SINGLE : DayView.THREE);
        }}
      />
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  headerContainer: {
    backgroundColor: theme.palette.common.white,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px",
    borderRadius: theme.spacing(1.5, 1.5, 0, 0),
  },
}));
